import { useEffect, useRef, useCallback } from "react";
import { useState } from "react";
import { toast } from "react-toastify";
import { tokenRefreshManager } from "../utils/tokenRefresh";
import { getAuthTokens } from "../utils/cookieUtils.js";

const AUTH_CLOSE_CODES = new Set([4001, 4003]);
const CONNECT_TIMEOUT_MS = 10_000;
const MIN_RECONNECT_DELAY_MS = 2_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const MIN_CONNECT_GAP_MS = 2_000;
const STABLE_CONNECTION_MS = 8_000;
const BURST_RECONNECT_ATTEMPTS = 3;
const PING_INTERVAL_MS = 300_000;
const HEALTH_CHECK_INTERVAL_MS = 5_000;


class WebSocketService {
  constructor() {
    this.client = null;
    this.isConnected = false;
    this.isConnecting = false;

    this.pingInterval = null;
    this.healthCheckInterval = null;

    this.chunkedPayloads = new Map();
    this.listeners = new Map();

    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = BURST_RECONNECT_ATTEMPTS;
    this.isReconnecting = false;
    this.isReconnectPaused = false;
    this.showManualReconnect = false;

    this.lastConnectionUrl = null;
    this.lastCloseEvent = null;
    this.didRefreshForCurrentDisconnect = false;
    this.manualDisconnect = false;
    this.hasReceivedSeedData = false;
    this.hasEstablishedConnection = false;
    this.hasEmittedDisconnect = false;
    this.isFrozen = false;
    this.networkListenersAttached = false;
    this.connectGeneration = 0;
    this.pendingConnectReject = null;
    this.reconnectStartedAt = 0;
    this.watchdogInterval = null;
    this.sawBrowserOffline = false;
    this.lastConnectAttemptAt = 0;
    this.connectionOpenedAt = 0;
    this.stableConnectionTimer = null;
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  emit(event, data) {
    const callbacks = this.listeners.get(event) || [];
    callbacks.forEach((callback) => callback(data));
  }

  removeListener(event, callback) {
    const callbacks = this.listeners.get(event) || [];
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  removeAllListeners() {
    this.listeners.clear();
  }

  isSocketOpen() {
    return Boolean(this.client && this.client.readyState === WebSocket.OPEN);
  }

  // Chrome DevTools Offline leaves readyState OPEN even though the socket is dead.
  isHealthySocket() {
    return this.isConnected && this.isSocketOpen() && !this.sawBrowserOffline;
  }

  isTabActive() {
    if (typeof document === "undefined") return false;
    if (this.isFrozen) return false;
    if (document.hidden || document.visibilityState !== "visible") return false;
    return true;
  }

  // WebSocket attempts should not trust navigator.onLine. Chrome DevTools
  // "Offline" throttling often leaves it false after you switch back to
  // No throttling, which would pause reconnect forever.
  isPageSafeToConnect() {
    return this.isTabActive();
  }


  buildConnectionUrl() {
    const user = getAuthTokens();
    const base = String(process.env.REACT_APP_URL || "").trim();
    if (base && user?.accessToken) {
      return `${base}?token=${user.accessToken}&client=browser`;
    }
    return this.lastConnectionUrl;
  }

  isAuthCloseEvent(event) {
    if (!event) return false;
    const reason = String(event.reason || "").toLowerCase();
    return (
      AUTH_CLOSE_CODES.has(event.code) ||
      reason.includes("token") ||
      reason.includes("auth")
    );
  }

  detachSocketHandlers(socket) {
    if (!socket) return;
    socket.onopen = null;
    socket.onerror = null;
    socket.onclose = null;
    socket.onmessage = null;
  }

  abandonInFlightConnect(reason = "Connection attempt aborted") {
    this.connectGeneration += 1;
    const reject = this.pendingConnectReject;
    this.pendingConnectReject = null;

    const socket = this.client;
    this.client = null;
    this.isConnecting = false;

    if (socket) {
      this.detachSocketHandlers(socket);
      try {
        socket.close();
      } catch (_) {
        // Socket may already be closing.
      }
    }

    if (reject) {
      reject(new Error(reason));
    }
  }

  async connect(url) {
    this.lastConnectionUrl = url;
    this.manualDisconnect = false;
    const generation = ++this.connectGeneration;
    this.isConnecting = true;
    this.lastConnectAttemptAt = Date.now();

    this.setupNetworkListeners();

    if (this.pendingConnectReject) {
      this.pendingConnectReject(new Error("Superseded by a new connection attempt"));
    }

    if (this.client) {
      this.detachSocketHandlers(this.client);
      if (
        this.client.readyState === WebSocket.OPEN ||
        this.client.readyState === WebSocket.CONNECTING
      ) {
        try {
          this.client.close();
        } catch (_) {
          // Ignore close errors on a replacing socket.
        }
      }
      this.client = null;
    }


    try {
      this.client = new WebSocket(url);
    } catch (error) {
      this.isConnecting = false;
      this.pendingConnectReject = null;
      toast.error("WebSocket connection failed");
      throw error;
    }

    const connectionPromise = new Promise((resolve, reject) => {
      let settled = false;
      this.pendingConnectReject = (error) => {
        if (settled) return;
        settled = true;
        this.pendingConnectReject = null;
        this.isConnecting = false;
        reject(error);
      };

      this.client.onopen = () => {
        if (settled || generation !== this.connectGeneration) {
          return;
        }
        settled = true;
        this.pendingConnectReject = null;
        this.isConnected = true;
        this.hasEstablishedConnection = true;
        this.hasEmittedDisconnect = false;
        this.isReconnectPaused = false;
        this.showManualReconnect = false;
        this.sawBrowserOffline = false;
        this.connectionOpenedAt = Date.now();
        // Only clear failure backoff after the socket stays open long enough.
        // Quick open→close flaps used to reset attempts and schedule immediate
        // reconnects forever (ERR_INSUFFICIENT_RESOURCES).
        this.armStableConnectionReset();
        this.stopDisconnectWatchdog();
        this.startPingInterval();
        this.setupPermanentEventHandlers();
        this.emit("connect");
        resolve();
      };

      this.client.onerror = (error) => {
        if (settled || generation !== this.connectGeneration) return;
        settled = true;
        this.pendingConnectReject = null;
        this.isConnected = false;
        this.emit("error", error);
        reject(error);
      };

      this.client.onclose = (event) => {
        if (settled || generation !== this.connectGeneration) return;
        settled = true;
        this.pendingConnectReject = null;
        this.isConnected = false;
        this.lastCloseEvent = event;
        const error = new Error(
          `Connection closed: ${event.code} ${event.reason || ""}`
        );
        this.emit("error", error);
        reject(error);
      };
    });

    try {
      await connectionPromise;
    } finally {
      if (generation === this.connectGeneration) {
        this.isConnecting = false;
        this.pendingConnectReject = null;
      }
    }
  }

  removeNetworkListeners() {
    if (this.onlineHandler) {
      window.removeEventListener("online", this.onlineHandler);
    }
    if (this.offlineHandler) {
      window.removeEventListener("offline", this.offlineHandler);
    }
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
    }
    if (this.freezeHandler) {
      document.removeEventListener("freeze", this.freezeHandler);
    }
    if (this.resumeHandler) {
      document.removeEventListener("resume", this.resumeHandler);
    }
    if (this.connectionHandler && navigator.connection) {
      navigator.connection.removeEventListener("change", this.connectionHandler);
    }
    this.networkListenersAttached = false;
  }

  setupNetworkListeners() {
    if (this.networkListenersAttached) return;

    this.onlineHandler = () => {
      this.sawBrowserOffline = false;
      this.kickReconnect("online");
    };

    this.offlineHandler = () => {
      this.sawBrowserOffline = true;
      this.isConnected = false;
      if (!this.hasEmittedDisconnect) {
        this.hasEmittedDisconnect = true;
        this.emit("error", new Error("Network connection lost"));
      }
      this.abandonInFlightConnect("browser offline");
      // Do not open sockets while offline — wait for the online event.
      this.pauseReconnect("offline");
    };

    this.visibilityHandler = () => {
      if (document.hidden) {
        this.clearReconnectTimer();
        if (!this.isHealthySocket()) {
          this.pauseReconnect("hidden");
        }
        return;
      }

      if (this.isHealthySocket()) {
        this.send("ping");
        return;
      }

      this.kickReconnect("visible");
    };

    this.freezeHandler = () => {
      this.isFrozen = true;
      this.clearReconnectTimer();
    };

    this.resumeHandler = () => {
      this.isFrozen = false;
      if (this.isHealthySocket()) {
        this.send("ping");
        return;
      }
      this.kickReconnect("resume");
    };

    this.connectionHandler = () => {
      if (this.isHealthySocket()) return;
      this.kickReconnect("connection-change");
    };

    window.addEventListener("online", this.onlineHandler);
    window.addEventListener("offline", this.offlineHandler);
    document.addEventListener("visibilitychange", this.visibilityHandler);
    document.addEventListener("freeze", this.freezeHandler);
    document.addEventListener("resume", this.resumeHandler);
    if (navigator.connection && typeof navigator.connection.addEventListener === "function") {
      navigator.connection.addEventListener("change", this.connectionHandler);
    }
    this.networkListenersAttached = true;
  }

  setupPermanentEventHandlers() {
    if (!this.client) return;

    this.client.onerror = () => {
      // Wait for onclose for a single disconnect transition.
    };

    this.client.onclose = (event) => {
      this.handleSocketClosed(event);
    };

    this.client.onmessage = (event) => {
      this.handleMessage(event);
    };
  }

  handleSocketClosed(event) {
    this.isConnected = false;
    this.lastCloseEvent = event || this.lastCloseEvent;
    this.didRefreshForCurrentDisconnect = false;
    this.clearHealthTimers();
    this.clearStableConnectionTimer();

    const openedAt = this.connectionOpenedAt;
    this.connectionOpenedAt = 0;
    const openDurationMs = openedAt ? Date.now() - openedAt : 0;
    const wasStable = openDurationMs >= STABLE_CONNECTION_MS;

    // Flapping (open then close quickly) must keep backoff. A long-lived
    // connection that drops can try again promptly once.
    if (wasStable) {
      this.reconnectAttempts = 0;
    } else if (openedAt && this.reconnectAttempts === 0) {
      // Count the short-lived open as a failed cycle so the next schedule backs off.
      this.reconnectAttempts = 1;
    }

    this.startDisconnectWatchdog();

    if (!this.hasEmittedDisconnect) {
      this.hasEmittedDisconnect = true;
      this.emit("close", event);
    }

    if (!this.manualDisconnect && !this.isReconnecting) {
      this.scheduleReconnect({ immediate: wasStable });
    }
  }

  handleMessage(event) {
    try {
      const data = JSON.parse(event.data);

      if (data.action === "deployment" || data.type === "server_restart") {
        this.handleDeploymentNotification(data);
        return;
      }

      if (data.isChunked) {
        this.handleChunkedMessage(data);
        return;
      }

      this.emit("message", data);
    } catch (error) {
      console.error("Error parsing WebSocket message:", error);
      this.emit("error", error);
    }
  }

  handleDeploymentNotification(data) {
    this.emit("deployment", {
      message: data.message || "Server is restarting for deployment",
      estimatedDowntime: data.estimatedDowntime || 30,
      canReconnect: data.canReconnect !== false,
    });
  }

  handleChunkedMessage(data) {
    const { messageId, chunkIndex, totalChunks, chunkData, action } = data;

    if (!this.chunkedPayloads.has(messageId)) {
      this.chunkedPayloads.set(messageId, {
        chunks: [],
        received: 0,
        total: totalChunks,
        action,
      });
    }

    const payload = this.chunkedPayloads.get(messageId);
    payload.chunks[chunkIndex] = chunkData;
    payload.received += 1;

    if (payload.received === totalChunks) {
      const fullPayloadStr = payload.chunks.join("");
      try {
        const fullPayload = JSON.parse(fullPayloadStr);
        this.emit("message", fullPayload);
        this.chunkedPayloads.delete(messageId);
      } catch (error) {
        console.error("Failed to parse chunked payload:", error);
        this.emit("error", error);
        this.chunkedPayloads.delete(messageId);
      }
    }
  }

  send(message) {
    if (!this.client || this.client.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket is not connected. Message not sent:", message);
      return false;
    }

    try {
      const messageStr =
        typeof message === "string" ? message : JSON.stringify(message);
      this.client.send(messageStr);
      return true;
    } catch (error) {
      console.error("Failed to send WebSocket message:", error);
      return false;
    }
  }

  startPingInterval() {
    this.clearHealthTimers();

    this.pingInterval = setInterval(() => {
      if (!this.isConnected || !this.isSocketOpen()) return;
      const success = this.send("ping");
      if (!success) {
        this.handleSocketClosed({
          code: 1006,
          reason: "Ping failed",
        });
      }
    }, PING_INTERVAL_MS);

    this.healthCheckInterval = setInterval(() => {
      if (!this.isConnected || this.hasEmittedDisconnect) return;
      if (this.client && this.client.readyState !== WebSocket.OPEN) {
        this.handleSocketClosed({
          code: 1006,
          reason: "Health check: connection lost",
        });
      }
    }, HEALTH_CHECK_INTERVAL_MS);
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  clearStableConnectionTimer() {
    if (this.stableConnectionTimer) {
      clearTimeout(this.stableConnectionTimer);
      this.stableConnectionTimer = null;
    }
  }

  armStableConnectionReset() {
    this.clearStableConnectionTimer();
    this.stableConnectionTimer = setTimeout(() => {
      this.stableConnectionTimer = null;
      if (!this.isHealthySocket()) return;
      this.reconnectAttempts = 0;
      this.showManualReconnect = false;
    }, STABLE_CONNECTION_MS);
  }

  clearHealthTimers() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  stopPingInterval() {
    this.clearHealthTimers();
  }

  markSeedDataReceived() {
    this.hasReceivedSeedData = true;
    if (!this.isHealthySocket() && !this.manualDisconnect) {
      this.scheduleReconnect({ immediate: false });
    }
  }

  resetSeedDataFlag() {
    this.hasReceivedSeedData = false;
  }

  getBackoffDelay() {
    if (this.reconnectAttempts === 0) return 0;
    const delay =
      MIN_RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempts - 1);
    return Math.min(delay, MAX_RECONNECT_DELAY_MS);
  }

  pauseReconnect(reason = "paused") {
    this.clearReconnectTimer();
    this.isReconnecting = false;
    this.isReconnectPaused = true;
    this.startDisconnectWatchdog();
    this.emit("reconnectPaused", { reason });
  }

  kickReconnect(reason = "retry") {
    if (this.manualDisconnect) {
      return;
    }

    if (this.isHealthySocket()) {
      return;
    }

    if (this.isSocketOpen() && !this.isHealthySocket()) {
      try {
        this.client.close();
      } catch (_) {
        // Ignore close errors on a zombie socket.
      }
    }

    const attemptAge = this.reconnectStartedAt
      ? Date.now() - this.reconnectStartedAt
      : Infinity;
    const attemptInFlight = this.isReconnecting || this.isConnecting;
    if (attemptInFlight && attemptAge < CONNECT_TIMEOUT_MS) {
      return;
    }

    this.abandonInFlightConnect(`Retry triggered by ${reason}`);
    this.isReconnecting = false;
    this.isReconnectPaused = false;
    // Never reset backoff here — visibility/online/connection-change used to
    // zero attempts and force immediate connects in a tight loop.
    const preferSoon =
      (reason === "visible" || reason === "online" || reason === "resume") &&
      this.reconnectAttempts === 0;
    this.scheduleReconnect({ immediate: preferSoon });
  }

  startDisconnectWatchdog() {
    if (this.watchdogInterval || this.manualDisconnect) return;
    this.watchdogInterval = setInterval(() => {
      if (this.manualDisconnect) {
        this.stopDisconnectWatchdog();
        return;
      }
      if (this.isHealthySocket()) {
        this.stopDisconnectWatchdog();
        return;
      }
      if (this.sawBrowserOffline) {
        return;
      }
      if (!this.isPageSafeToConnect()) return;

      const stuckFor = this.reconnectStartedAt
        ? Date.now() - this.reconnectStartedAt
        : Infinity;
      if (
        (this.isReconnecting || this.isConnecting) &&
        stuckFor > CONNECT_TIMEOUT_MS + 2000
      ) {
        this.kickReconnect("watchdog-stuck");
        return;
      }

      if (this.isReconnecting || this.isConnecting || this.reconnectTimer) {
        return;
      }

      this.scheduleReconnect({ immediate: false });
    }, 3000);
  }

  stopDisconnectWatchdog() {
    if (this.watchdogInterval) {
      clearInterval(this.watchdogInterval);
      this.watchdogInterval = null;
    }
  }

  canAutoReconnect() {
    if (this.manualDisconnect) return false;
    if (!getAuthTokens()?.accessToken) return false;
    if (!this.buildConnectionUrl() && !this.lastConnectionUrl) return false;
    return true;
  }

  scheduleReconnect({ immediate = false } = {}) {
    if (!this.canAutoReconnect()) {
      return;
    }
    if (this.sawBrowserOffline) {
      this.pauseReconnect("offline");
      return;
    }
    if (this.isReconnecting || this.isConnecting) {
      return;
    }

    if (!this.isPageSafeToConnect()) {
      const reason = this.isFrozen ? "frozen" : "hidden";
      this.pauseReconnect(reason);
      return;
    }

    this.isReconnectPaused = false;

    if (this.reconnectTimer && !immediate) {
      return;
    }

    const sinceLastConnect = this.lastConnectAttemptAt
      ? Date.now() - this.lastConnectAttemptAt
      : Infinity;
    const gapNeeded = Math.max(0, MIN_CONNECT_GAP_MS - sinceLastConnect);
    const backoff = this.getBackoffDelay();
    // Even "immediate" must respect the connect gap so we cannot spawn sockets
    // faster than the browser can reclaim them.
    const delay = immediate
      ? gapNeeded
      : Math.max(backoff, gapNeeded);

    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnect();
    }, delay);
    this.emit("reconnecting", {
      attempt: Math.max(this.reconnectAttempts, 1),
      maxAttempts: this.maxReconnectAttempts,
      delay,
    });
  }

  async maybeRefreshTokenForReconnect() {
    if (!this.isPageSafeToConnect()) {
      return;
    }

    const currentUser = getAuthTokens();
    if (!currentUser) {
      return;
    }

    const authClose = this.isAuthCloseEvent(this.lastCloseEvent);
    const tokenNearExpiry =
      tokenRefreshManager.isAccessTokenNearExpiry(currentUser);

    if (!authClose && !tokenNearExpiry) {
      return;
    }
    if (this.didRefreshForCurrentDisconnect && !tokenNearExpiry) {
      return;
    }

    try {
      if (authClose && !this.didRefreshForCurrentDisconnect) {
        await tokenRefreshManager.refreshUserToken(currentUser);
      } else {
        await tokenRefreshManager.ensureFreshToken(currentUser);
      }
      this.didRefreshForCurrentDisconnect = true;
    } catch (refreshError) {
      if (refreshError?.code === "SESSION_EXPIRED") {
        this.showManualReconnect = true;
        this.emit("reconnectFailed", {
          attempts: this.reconnectAttempts,
          reason: refreshError.message,
        });
      }
    }
  }

  async reconnect(user = null, options = {}) {
    const manual = options?.manual === true;

    if (this.manualDisconnect) {
      return false;
    }

    const connectionUrl = this.buildConnectionUrl() || this.lastConnectionUrl;
    if (!connectionUrl) {
      this.showManualReconnect = true;
      this.emit("reconnectFailed", {
        attempts: this.reconnectAttempts,
        reason: "Missing connection URL or access token",
      });
      return false;
    }
    this.lastConnectionUrl = connectionUrl;

    if (this.isReconnecting) {
      return false;
    }

    if (!this.isPageSafeToConnect()) {
      this.pauseReconnect(this.isFrozen ? "frozen" : "hidden");
      return false;
    }

    if (manual) {
      this.reconnectAttempts = 0;
      this.showManualReconnect = false;
    }

    this.clearReconnectTimer();
    this.isReconnecting = true;
    this.isReconnectPaused = false;
    this.reconnectAttempts += 1;
    this.reconnectStartedAt = Date.now();
    this.startDisconnectWatchdog();


    this.emit("reconnecting", {
      attempt: this.reconnectAttempts,
      maxAttempts: this.maxReconnectAttempts,
    });

    let connectionPromise = null;
    try {
      await this.maybeRefreshTokenForReconnect();

      const latestUser = getAuthTokens();
      if (latestUser?.accessToken) {
        this.lastConnectionUrl = this.buildConnectionUrl() || this.lastConnectionUrl;
      }

      connectionPromise = this.connect(this.lastConnectionUrl);
      connectionPromise.catch(() => {});

      let timeoutId;
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(
            new Error(
              `Connection attempt timed out after ${CONNECT_TIMEOUT_MS / 1000} seconds`
            )
          );
        }, CONNECT_TIMEOUT_MS);
      });
      timeoutPromise.catch(() => {});

      try {
        await Promise.race([connectionPromise, timeoutPromise]);
      } finally {
        clearTimeout(timeoutId);
      }
      this.isReconnecting = false;
      // Keep reconnectAttempts until the connection proves stable.
      this.reconnectStartedAt = 0;
      this.showManualReconnect = false;
      this.emit("reconnectSuccess");
      return true;
    } catch (_error) {
      this.abandonInFlightConnect("Reconnect attempt failed");
      this.isReconnecting = false;
      this.isConnecting = false;
      this.reconnectStartedAt = 0;
      this.clearStableConnectionTimer();
      this.connectionOpenedAt = 0;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        this.showManualReconnect = true;
        this.emit("reconnectFailed", {
          attempts: this.reconnectAttempts,
          reason: `Failed after ${this.maxReconnectAttempts} attempts`,
        });
      }

      if (!this.manualDisconnect) {
        this.scheduleReconnect();
      }

      return false;
    }
  }

  disconnect() {
    this.manualDisconnect = true;
    this.isConnected = false;
    this.isConnecting = false;
    this.isReconnecting = false;
    this.isReconnectPaused = false;
    this.showManualReconnect = false;
    this.reconnectAttempts = 0;
    this.hasReceivedSeedData = false;
    this.hasEstablishedConnection = false;
    this.hasEmittedDisconnect = false;
    this.lastConnectionUrl = null;
    this.reconnectStartedAt = 0;
    this.connectionOpenedAt = 0;
    this.lastConnectAttemptAt = 0;
    this.clearReconnectTimer();
    this.clearStableConnectionTimer();
    this.stopDisconnectWatchdog();
    this.abandonInFlightConnect("Manual disconnect");
    this.clearHealthTimers();
    this.removeNetworkListeners();

    this.chunkedPayloads.clear();
    this.removeAllListeners();
  }

  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      readyState: this.client?.readyState,
      networkOnline: navigator.onLine,
      isReconnecting: this.isReconnecting || Boolean(this.reconnectTimer),
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      isReconnectPaused: this.isReconnectPaused,
      showManualReconnect: this.showManualReconnect,
    };
  }
}

const webSocketService = new WebSocketService();

export const useWebSocket = () => {
  const listenersRef = useRef(new Map());

  const cleanup = useCallback(() => {
    listenersRef.current.forEach((listener, event) => {
      webSocketService.removeListener(event, listener);
    });
    listenersRef.current.clear();
  }, []);

  const subscribe = useCallback((event, callback) => {
    if (listenersRef.current.has(event)) {
      webSocketService.removeListener(event, listenersRef.current.get(event));
    }

    webSocketService.on(event, callback);
    listenersRef.current.set(event, callback);
  }, []);

  const subscribeToMessage = useCallback((action, callback) => {
    const listener = (data) => {
      if (data.action === action) {
        callback(data);
      }
    };

    const key = `message:${action}`;
    if (listenersRef.current.has(key)) {
      webSocketService.removeListener("message", listenersRef.current.get(key));
    }

    webSocketService.on("message", listener);
    listenersRef.current.set(key, listener);
  }, []);

  const send = useCallback((message) => {
    return webSocketService.send(message);
  }, []);

  const getStatus = useCallback(() => {
    return webSocketService.getConnectionStatus();
  }, []);

  const connect = useCallback(async (url) => {
    return webSocketService.connect(url);
  }, []);

  const disconnect = useCallback(() => {
    webSocketService.disconnect();
  }, []);

  const reconnect = useCallback(async (user = null, options = {}) => {
    return webSocketService.reconnect(user, options);
  }, []);

  const handleReconnecting = useCallback(() => {}, []);

  const handleReconnectFailed = useCallback((data) => {
    if (navigator.onLine) {
      console.error(
        "Reconnection failed after",
        data.attempts,
        "attempts:",
        data.reason
      );
    }
  }, []);

  useEffect(() => {
    webSocketService.on("reconnecting", handleReconnecting);
    webSocketService.on("reconnectFailed", handleReconnectFailed);

    return () => {
      webSocketService.removeListener("reconnecting", handleReconnecting);
      webSocketService.removeListener("reconnectFailed", handleReconnectFailed);
      cleanup();
    };
  }, [cleanup, handleReconnecting, handleReconnectFailed]);

  return {
    send,
    connect,
    disconnect,
    reconnect,
    subscribe,
    subscribeToMessage,
    getStatus,
    isConnected: webSocketService.isConnected,
    isReconnecting: webSocketService.isReconnecting,
    reconnectAttempts: webSocketService.reconnectAttempts,
    maxReconnectAttempts: webSocketService.maxReconnectAttempts,
    onMessage: subscribeToMessage,
    onConnect: (callback) => subscribe("connect", callback),
    onDisconnect: (callback) => subscribe("close", callback),
    onError: (callback) => subscribe("error", callback),
    onReconnecting: (callback) => subscribe("reconnecting", callback),
    onReconnectSuccess: (callback) => subscribe("reconnectSuccess", callback),
    onReconnectFailed: (callback) => subscribe("reconnectFailed", callback),
    onDeployment: (callback) => subscribe("deployment", callback),
    onAuthError: (callback) => subscribe("authError", callback),
    markSeedDataReceived: () => webSocketService.markSeedDataReceived(),
    resetSeedDataFlag: () => webSocketService.resetSeedDataFlag(),
  };
};

export const useWebSocketMessage = (action, callback) => {
  const { subscribeToMessage } = useWebSocket();

  useEffect(() => {
    if (action && callback) {
      subscribeToMessage(action, callback);
    }
  }, [action, callback, subscribeToMessage]);
};

export const useWebSocketStatus = () => {
  const [status, setStatus] = useState(webSocketService.getConnectionStatus());

  useEffect(() => {
    const updateStatus = () =>
      setStatus(webSocketService.getConnectionStatus());

    webSocketService.on("connect", updateStatus);
    webSocketService.on("close", updateStatus);
    webSocketService.on("error", updateStatus);
    webSocketService.on("reconnecting", updateStatus);
    webSocketService.on("reconnectSuccess", updateStatus);
    webSocketService.on("reconnectFailed", updateStatus);
    webSocketService.on("reconnectPaused", updateStatus);

    return () => {
      webSocketService.removeListener("connect", updateStatus);
      webSocketService.removeListener("close", updateStatus);
      webSocketService.removeListener("error", updateStatus);
      webSocketService.removeListener("reconnecting", updateStatus);
      webSocketService.removeListener("reconnectSuccess", updateStatus);
      webSocketService.removeListener("reconnectFailed", updateStatus);
      webSocketService.removeListener("reconnectPaused", updateStatus);
    };
  }, []);

  return status;
};

export { webSocketService };

export const sendWebSocketMessage = (message) => {
  const success = webSocketService.send(message);
  if (!success) {
    console.warn("WebSocket message not sent - connection not available");
    webSocketService.emit("sendFailed", { message, timestamp: Date.now() });
  }
  return success;
};

export const isWebSocketConnected = () => webSocketService.isConnected;
export const getWebSocketStatus = () => webSocketService.getConnectionStatus();
